// ─────────────────────────────────────────────────────────────────────────────
// Party engine — 10-slot player palette (DESIGN-party-engine.md §5)
//
// The SERVER deals a color slot per player (one each, no duplicates, returned
// to the pool on leave). The FRONTEND owns the mapping slot → CSS. This module
// is the single definition of that mapping; nothing else may hard-code a
// player color.
//
// Derived from the brand palette (resources/brand/tokens.json) and extended
// from Trivia Night's 8 tones to 10. Every tone carries an `ink` color chosen
// for ≥ 4.5:1 contrast (WCAG AA) against the tone itself, and all ten are
// ≥ 25 ΔE76 apart from each other so no two players are confusable.
//
// Deviation from §5's "extend the existing 8-tone set": brand `accent-cream`
// (#FFF4C2) was dropped as a player tone — it scores 1.05:1 against the cream
// surface, so a cream badge reads as a hole in the page. It is replaced by
// magenta, plus orange and teal, to reach ten. Cream remains available as a
// surface token; it is just not a player identity.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What the server puts in `players[].color`. Accepted as a palette id
 * ("yellow"), a slot index (0–9), a numeric string ("3"), or a raw hex —
 * `getPlayerColor` normalizes all four so a backend convention change cannot
 * break the UI.
 */
export type ColorSlot = string | number;

export interface PlayerColor {
  /** Stable palette id — what the server should ideally deal. */
  id: string;
  /** Human label (used in aria-labels). */
  name: string;
  /** Slot index 0–9. */
  index: number;
  /** Fill. */
  hex: string;
  /** Text/icon color to use ON the fill (≥ 4.5:1 against `hex`). */
  ink: string;
}

/** The ten dealt tones, in deal order (adjacent slots are far apart in hue). */
export const PLAYER_PALETTE: readonly PlayerColor[] = [
  { id: "yellow", name: "Yellow", index: 0, hex: "#FFD700", ink: "#000000" },
  { id: "blue", name: "Blue", index: 1, hex: "#00BFFF", ink: "#000000" },
  { id: "red", name: "Red", index: 2, hex: "#FF4500", ink: "#000000" },
  { id: "green", name: "Green", index: 3, hex: "#A0E400", ink: "#000000" },
  { id: "purple", name: "Purple", index: 4, hex: "#4A3AFF", ink: "#FFFFFF" },
  { id: "orange", name: "Orange", index: 5, hex: "#FF8A00", ink: "#000000" },
  { id: "pink", name: "Pink", index: 6, hex: "#FF8AB8", ink: "#000000" },
  { id: "teal", name: "Teal", index: 7, hex: "#00A88E", ink: "#000000" },
  { id: "magenta", name: "Magenta", index: 8, hex: "#D6009B", ink: "#FFFFFF" },
  { id: "lavender", name: "Lavender", index: 9, hex: "#B39DDB", ink: "#000000" },
] as const;

export const PLAYER_PALETTE_SIZE = PLAYER_PALETTE.length;

/** Fallback for a player whose color never arrived (should not happen). */
export const NEUTRAL_PLAYER_COLOR: PlayerColor = {
  id: "neutral",
  name: "Grey",
  index: -1,
  hex: "#D7D7D7",
  ink: "#000000",
};

/** Greyed-out tone for `connected: false`. */
export const DISCONNECTED_COLOR: PlayerColor = {
  id: "offline",
  name: "Offline",
  index: -1,
  hex: "#C9C9C9",
  ink: "#5A5A5A",
};

const BY_ID = new Map(PLAYER_PALETTE.map((c) => [c.id, c]));
const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Normalize whatever the server dealt into a renderable tone.
 * Unknown-but-stable values are hashed into the palette rather than dropped,
 * so two players never collide silently on the neutral tone.
 */
export function getPlayerColor(slot: ColorSlot | null | undefined): PlayerColor {
  if (slot === null || slot === undefined || slot === "") return NEUTRAL_PLAYER_COLOR;

  if (typeof slot === "number") {
    if (!Number.isFinite(slot)) return NEUTRAL_PLAYER_COLOR;
    const i = ((Math.trunc(slot) % PLAYER_PALETTE_SIZE) + PLAYER_PALETTE_SIZE) % PLAYER_PALETTE_SIZE;
    return PLAYER_PALETTE[i];
  }

  const key = slot.trim().toLowerCase();
  const byId = BY_ID.get(key);
  if (byId) return byId;

  if (/^-?\d+$/.test(key)) return getPlayerColor(parseInt(key, 10));

  if (HEX_RE.test(key)) {
    const hex = expandHex(key);
    const known = PLAYER_PALETTE.find((c) => c.hex.toLowerCase() === hex);
    if (known) return known;
    return { id: hex, name: "Custom", index: -1, hex, ink: bestInkOn(hex) };
  }

  return PLAYER_PALETTE[hashSlot(key) % PLAYER_PALETTE_SIZE];
}

/** Deterministic tone for an id when no color was dealt at all. */
export function fallbackColorFor(id: string): PlayerColor {
  return PLAYER_PALETTE[hashSlot(id) % PLAYER_PALETTE_SIZE];
}

// ── Session-dealt roster colours (§ F79.b) ───────────────────────────────────
//
// `fallbackColorFor` hashes an id into ten slots, and the family roster's five
// UUIDs actually collide twice (JAKE==MUMMY, JACK==ELLIOTT) — the same two
// pairs, in the same colours, on every load, forever. Jake: the "Who Are You?"
// screens must RANDOMIZE the colours and two players must NEVER share one.
//
// So the daily challenges surface deals from a DECK instead: the ten-tone palette is
// shuffled once per page load, and each player id is dealt the next card the
// first time any screen asks after it. Within a load a player keeps their
// colour on every screen (picker, family rows, history); the next load deals
// afresh. Repeats are impossible until the deck runs out, and the deck is ten
// deep for a roster of five — the wrap at ten players is a ceiling, not a
// behaviour anybody should ever see.

let sessionDeck: readonly PlayerColor[] = shuffledPalette();
const sessionDealt = new Map<string, PlayerColor>();

function shuffledPalette(): PlayerColor[] {
  const deck = [...PLAYER_PALETTE];
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}

/** Session-stable, collision-free, reshuffled every page load. */
export function sessionColorFor(id: string): PlayerColor {
  let tone = sessionDealt.get(id);
  if (!tone) {
    tone = sessionDeck[sessionDealt.size % sessionDeck.length];
    sessionDealt.set(id, tone);
  }
  return tone;
}

/** Test seam — re-deal as a fresh page load would. */
export function resetSessionColors(): void {
  sessionDeck = shuffledPalette();
  sessionDealt.clear();
}

function hashSlot(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

function expandHex(hex: string): string {
  if (hex.length === 4) return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  return hex;
}

// ── contrast math (also used by the palette tests) ────────────────────────────

export function hexToRgb(hex: string): [number, number, number] {
  const h = expandHex(hex.trim());
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/** Black or white — whichever reads better on `hex`. */
export function bestInkOn(hex: string): string {
  return contrastRatio(hex, "#000000") >= contrastRatio(hex, "#FFFFFF") ? "#000000" : "#FFFFFF";
}

/** `hex` + alpha, for tinted rows/backgrounds. */
export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
}

// ── pens ─────────────────────────────────────────────────────────────────────

export interface PenColor {
  id: string;
  name: string;
  hex: string;
}

/**
 * The six pens on the full DrawCanvas (design §6). Brand-derived, but each pen
 * is darkened until it clears 3:1 against the white paper — a stroke you can't
 * see isn't a brand color, it's a bug. Brand yellow (1.33:1 on paper) is
 * excluded from the pen set entirely for the same reason; it stays a player
 * identity tone, where it sits behind a 4px ink border.
 */
export const INK_COLORS: readonly PenColor[] = [
  { id: "ink", name: "Black", hex: "#000000" },
  { id: "red", name: "Red", hex: "#FF4500" },
  { id: "orange", name: "Orange", hex: "#E06A00" },
  { id: "green", name: "Green", hex: "#4A9400" },
  { id: "blue", name: "Blue", hex: "#0091E0" },
  { id: "purple", name: "Purple", hex: "#4A3AFF" },
] as const;

export const DEFAULT_PEN = INK_COLORS[0];

/** Canvas paper + border tokens, mirrored from index.css. */
export const CANVAS_PAPER = "#FFFFFF";
export const INK_BORDER = "#000000";
